import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    ReactNode,
} from 'react';
import {
    NotificationItem,
    listNotifications,
    markAllNotificationsRead,
    markNotificationRead,
} from '../api/notifications';
import { useAuth } from './AuthContext';

interface ToastItem extends NotificationItem {
    /** Local id for stacking (mirrors notification id, but tolerant of duplicates). */
    toastKey: string;
}

interface NotificationContextValue {
    notifications: NotificationItem[];
    unreadCount: number;
    loading: boolean;
    toasts: ToastItem[];
    refresh: () => Promise<void>;
    markRead: (id: number) => Promise<void>;
    markAllRead: () => Promise<void>;
    dismissToast: (toastKey: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const POLL_INTERVAL_MS = 20_000;
const MAX_TOAST_STACK = 3;
const FETCH_LIMIT = 25;

export function NotificationProvider({ children }: { children: ReactNode }): React.ReactElement {
    const { isAuthenticated } = useAuth();
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    /** Tracks the highest id we've already seen so polling can detect new arrivals. */
    const seenMaxIdRef = useRef<number>(0);
    /** First load should not flash all existing items as toasts. */
    const initialLoadRef = useRef<boolean>(true);

    const refresh = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }
        setLoading(true);
        try {
            const data = await listNotifications({ limit: FETCH_LIMIT });
            const incoming = data.items || [];
            setNotifications(incoming);
            setUnreadCount(data.unread_count || 0);

            if (initialLoadRef.current) {
                initialLoadRef.current = false;
                seenMaxIdRef.current = incoming.reduce(
                    (max, item) => (item.id > max ? item.id : max),
                    0,
                );
                return;
            }

            const newToasts = incoming
                .filter((item) => item.id > seenMaxIdRef.current)
                .filter((item) => item.status === 'success' || item.status === 'failure' || item.status === 'in_progress')
                .reverse() // oldest first so newest stacks on top
                .map<ToastItem>((item) => ({
                    ...item,
                    toastKey: `n-${item.id}-${Date.now()}`,
                }));

            if (newToasts.length > 0) {
                setToasts((current) => [...current, ...newToasts].slice(-MAX_TOAST_STACK));
                seenMaxIdRef.current = incoming.reduce(
                    (max, item) => (item.id > max ? item.id : max),
                    seenMaxIdRef.current,
                );
            }
        } catch (err) {
            // Silently swallow — notifications must never block the UI.
            // eslint-disable-next-line no-console
            console.warn('Failed to load notifications', err);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    /** Reset internal state on auth change. */
    useEffect(() => {
        if (!isAuthenticated) {
            setNotifications([]);
            setUnreadCount(0);
            setToasts([]);
            seenMaxIdRef.current = 0;
            initialLoadRef.current = true;
            return;
        }
        // Kick off initial load and polling.
        refresh();
        const handle = window.setInterval(() => {
            refresh();
        }, POLL_INTERVAL_MS);
        return () => window.clearInterval(handle);
    }, [isAuthenticated, refresh]);

    const markRead = useCallback(async (id: number) => {
        try {
            await markNotificationRead(id);
            setNotifications((prev) =>
                prev.map((item) =>
                    item.id === id && !item.read_at ? { ...item, read_at: new Date().toISOString() } : item,
                ),
            );
            setUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Failed to mark notification read', err);
        }
    }, []);

    const markAllRead = useCallback(async () => {
        try {
            const updated = await markAllNotificationsRead();
            if (updated > 0) {
                const now = new Date().toISOString();
                setNotifications((prev) =>
                    prev.map((item) => (item.read_at ? item : { ...item, read_at: now })),
                );
                setUnreadCount(0);
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Failed to mark all notifications read', err);
        }
    }, []);

    const dismissToast = useCallback((toastKey: string) => {
        setToasts((prev) => prev.filter((toast) => toast.toastKey !== toastKey));
    }, []);

    const value = useMemo<NotificationContextValue>(
        () => ({
            notifications,
            unreadCount,
            loading,
            toasts,
            refresh,
            markRead,
            markAllRead,
            dismissToast,
        }),
        [notifications, unreadCount, loading, toasts, refresh, markRead, markAllRead, dismissToast],
    );

    return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications(): NotificationContextValue {
    const ctx = useContext(NotificationContext);
    if (!ctx) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return ctx;
}
